<template>
  <div :id="`video-room-widget-${roomId}`" class="video-room-widget">
    <div v-if="error">{{ error }}</div>
    <div v-else class="video-room-widget__media-container" :class="{ 'ui-optimized': isUIOptimized }">
      <div class="media-controls-container">
        <div 
          v-for="participant in participantInfo" 
          :key="participant.participantIndex" 
          class="media-container" 
          :class="{
            'active': participant.joined && !participant.anonymous,
            'inactive': !participant.joined || participant.anonymous || isUIOptimized,
            'full-screen': participant.joined && !participant.anonymous && numberOfJoinedParticipants == 2,
          }"
        >
          <div v-if="participant.joined" class="media-participant-top">
            <span v-if="!participant.anonymous" class="media-participant-identity-label">
              {{ participant.identity }}
            </span>
            <div v-if="!magicToken && !participant.anonymous" class="media-participant-settings">
              <b-button class="mx-1" size="xs" pill variant="info" @click="enableRemoteAudio(participant)">
                {{ !participant.audio ? 'Unmute' : 'Mute' }}
              </b-button>
              <b-dropdown size="xs" variant="info" no-caret class="convey-video-setting">
                <template #button-content>
                  <b-icon-three-dots />
                </template>
                <b-dropdown-item-button @click="enableRemoteAudio(participant)">
                  {{ !participant.audio ? 'Unmute' : 'Mute' }}
                </b-dropdown-item-button>
                <b-dropdown-item-button @click="enableRemoteVideo(participant)">
                  {{ !participant.video ? 'Enable Remote Video' : 'Disable Remote Video' }}
                </b-dropdown-item-button>
                <b-dropdown-item-button
                  v-if="agencySettings['enableVideoRecipientControls'] && participant.video"
                  @click="toggleRemoteCamera(participant)"
                >
                  Switch Camera
                </b-dropdown-item-button>
                <b-dropdown-item-button @click="requestLocation(participant)">
                  Request Location
                </b-dropdown-item-button>
                <b-dropdown-item-button @click="blurRemoteVideo(participant)">
                  {{ !participant.blur ? 'Blur' : 'Unblur' }}
                </b-dropdown-item-button>
                <b-dropdown-item-button @click="removeParticipant(participant)">
                  Remove Participant
                </b-dropdown-item-button>
              </b-dropdown>
            </div>
          </div>
          <div v-if="participant.joined && !participant.anonymous" class="media-participant-bottom">
            <div class="media-participant-status">
              <b-icon-mic-mute-fill v-if="!participant.audio" scale="1" variant="danger" />
              <b-icon-camera-video-off-fill v-if="!participant.video" scale="1" variant="danger" />
            </div>
            <div class="media-participant-lang">
              <SelectLanguage :selfIdentity="selfIdentity" :participantIdentity="participant.identity"
                :magicToken="magicToken" @change="setParticipantLang" />
            </div>
          </div>
        </div>
      </div>
      <div 
        id="media-container-attachment" 
        class="media-container-attachment" 
        :style="{
          'background-image': mIsDispatch && numberOfJoinedParticipants < 2 ? `url(${agencySettings.agencyLogo})`: ''
        }"
      >
        <div 
          id="self-media-container" 
          class="media-container self-media-container" 
          :class="{
            'active': !isUIOptimized && isCameraOn,
            'inactive': isUIOptimized || !isCameraOn
          }"
        ></div>
        <div 
          v-for="participant in participantInfo" 
          :key="participant.participantIndex"
          :id="`media-container-${participant.participantIndex}`" 
          class="media-container remote-media-container" 
          :class="{
            'active': participant.joined && !participant.anonymous,
            'inactive': !participant.joined || participant.anonymous || isUIOptimized,
            'blurVideo': participant.blur && !magicToken && videoVisibilityStatus[participant.identity],
            'stripesBlurVideo': !magicToken && !videoVisibilityStatus[participant.identity],
            'full-screen': participant.joined && !participant.anonymous && numberOfJoinedParticipants == 2,
            'video-enabled': participant.video,
            'video-disabled': !participant.video,
          }" 
          :style="{
            'order': participant.order,
            'background-image': (participant.joined) ? mIsDispatch
              ? `url('/images/avatar-transparent.png')`
              : `url('/images/heroicon-badge.png')` : ''
          }"
        >
          <div class="media-participant-overlay" v-if="participant.joined && !participant.anonymous">
            {{ !magicToken && !videoVisibilityStatus[participant.identity] ? 'Recipient Video has been backgrounded' : '' }}
          </div>
        </div>
      </div>
    </div>
    <div v-if="transcribing" class="video-room-widget__transcription">
      <TranscriptionList :selfIdentity="selfIdentity" @showMore="showFullTranscriptions" />
      <div v-if="showFullTranscriptionList" class="video-room-widget__transcription--full">
        <TranscriptionFullList :selfIdentity="selfIdentity" @close="showFullTranscriptionList = false" />
      </div>
    </div>
    <div 
      v-if="!magicToken" 
      class="video-room-widget_switchcamera" 
      :class="{
        'show-list': isOpenRecipientCameraList && recipientCameraList.length > 0 && recipientIdentityForCameraList
      }" 
      :style="{
        'height': (isOpenRecipientCameraList && recipientCameraList.length > 0 && recipientIdentityForCameraList)
          ? getCameraListHeight(recipientCameraList)
          : '0px'
      }"
    >
      <RecipientCameraList 
        v-if="recipientCameraList.length > 0" 
        :identity="recipientIdentityForCameraList"
        :cameraList="recipientCameraList" 
        @selected="selectedRecipientCamera" 
        @close="closeRecipientCameraList" 
      />
    </div>
    <div v-show="mediaBtnContainerVisible" id="media-btn-container" class="video-room-widget__controls d-flex justify-content-between align-items-center px-1 py-1 px-md-3 py-md-2">
      <div class="d-flex align-items-center">
        <span v-if="!anonymous" class="mr-1 mr-md-4">
          <b-button @click="micOnBtnClick()" class="bg-transparent border-0 p-0" v-show="isMicOn">
            <b-icon-mic-fill scale="1" variant="success" />
          </b-button>
          <b-button @click="micOffBtnClick()" class="bg-transparent border-0 p-0" v-show="!isMicOn">
            <b-icon-mic-mute-fill scale="1" variant="danger" />
          </b-button>
          <b-dropdown no-caret variant="link" :dropright="!isMobile" class="device-select"
            :class="{ 'ui-optimized': isMobile }" toggle-class="p-0">
            <template #button-content>
              <b-icon-caret-down-fill scale="0.5" variant="dark" />
            </template>
            <b-dropdown-group id="outputdevice-group" header="Select Audio Output">
              <b-dropdown-item v-for="device in mVideoSettings.speakerList" :value="device.deviceId"
                :key="device.deviceId" @click="loadSpeaker(device.deviceId)">
                <div class="d-inline-block pl-2 mr-2" style="width:25px;">
                  <b-icon-check-2 v-if="mVideoSettings.speakerSelectedId == device.deviceId" variant="success" />
                </div>
                <span class="d-inline-block pr-2">{{ device.label }}</span>
              </b-dropdown-item>
            </b-dropdown-group>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-group id="inputdevice-group" header="Select Microphone">
              <b-dropdown-item v-for="device in mVideoSettings.micList" :value="device.deviceId" :key="device.deviceId"
                @click="loadMic(device.deviceId)">
                <div class="d-inline-block pl-2 mr-2" style="width:25px;">
                  <b-icon-check-2 v-if="mVideoSettings.micSelectedId == device.deviceId" variant="success" />
                </div>
                <span class="d-inline-block pr-2">{{ device.label }}</span>
              </b-dropdown-item>
            </b-dropdown-group>
          </b-dropdown>
        </span>
        <span v-if="!anonymous" class="mr-1 mr-md-4">
          <b-button @click="cameraOnBtnClick()" class="bg-transparent border-0 p-0" v-show="isCameraOn">
            <b-icon-camera-video-fill scale="1" variant="success" />
          </b-button>
          <b-button @click="cameraOffBtnClick()" class="bg-transparent border-0 p-0" v-show="!isCameraOn">
            <b-icon-camera-video-off-fill scale="1" variant="danger" />
          </b-button>
          <b-dropdown no-caret variant="link" :dropright="!isMobile" class="device-select"
            :class="{ 'ui-optimized': isMobile }" toggle-class="p-0">
            <template #button-content>
              <b-icon-caret-down-fill variant="dark" scale="0.5" />
            </template>
            <b-dropdown-item v-for="device in mVideoSettings.cameraList" :value="device.deviceId" :key="device.deviceId"
              @click="loadCamera(device.deviceId)">
              <div class="d-inline-block pl-2 mr-2" style="width:25px;">
                <b-icon-check-2 v-if="mVideoSettings.cameraSelectedId == device.deviceId" variant="success" />
              </div>
              <span class="d-inline-block pr-2">{{ device.label }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </span>
        <span v-if="!anonymous" class="mr-1 mr-md-4">
          <SelectLanguage :selfIdentity="selfIdentity" :participantIdentity="selfIdentity" :magicToken="magicToken"
            @change="setSelfLanguage" />
        </span>
      </div>
      <TranscriptionButton v-if="isTranscriptionAvailable" @startTranscription="agentStartTranscription"
        @stopTranscription="agentStopTranscription" />
      <div v-if="magicToken && magicToken.token_data.enableChat == 'enable'" class="d-flex align-items-center">
        <CallerChat :magicToken="magicToken" />
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import {
  mapGetters,
  mapState,
  mapActions
} from "vuex";
import {
  EventBus
} from "../../events/eventBus";
import {
  createLocalAudioTrack,
  createLocalVideoTrack,
  isSupported
} from "twilio-video";
import RecordRTC, { StereoAudioRecorder } from "recordrtc";
import {
  VideoService
} from "../../services/video_service";
import CallerChat from "../CallerChat";
import TranscriptionList from "../TranscriptionList";
import SelectLanguage from "../SelectLanguage";
import TranscriptionFullList from "../TranscriptionFullList";
import TranscriptionButton from "@/components/action_buttons/TranscriptionButton";
import RecipientCameraList from "@/components/RecipientCameraList";

export default {
  components: {
    CallerChat,
    TranscriptionList,
    SelectLanguage,
    TranscriptionFullList,
    TranscriptionButton,
    RecipientCameraList,
  },
  props: {
    roomId: String,
    token: String,
    resourceId: String,
    startLocalVideo: Boolean,
    startLocalAudio: Boolean,
    startTranscription: Boolean,
    blurVideo: Boolean,
    participantRole: String,
    agencyLogo: String,
    magicToken: Object,
    anonymous: Boolean,
    isMinimized: Boolean,
  },
  data() {
    return {
      error: null,
      isLoading: false,
      localTrack: false,
      remoteTrack: false,
      activeRoom: null,
      videoService: VideoService,
      mediaBtnContainerVisible: false,
      mediaDevices: [],
      mVideoSettings: {
        cameraList: [],
        micList: [],
        speakerList: [],
        cameraSelectedId: String,
        micSelectedId: String,
        speakerSelectedId: String,
      },
      isMicOn: false,
      isCameraOn: false,
      mIsDispatch: false,
      mediaBlockedLabelVisible: false,
      mediaContainerLoadingVisible: false,
      mediaCameraOffLabelVisible: true,
      isConnected: false,
      mediaContainerVisible: false,
      mediaErrorLabelVisible: false,
      mediaMaxAttemptsLabelVisible: false,
      mediaIsTimeoutLabelVisible: false,
      mediaWaitingLabelVisible: false,
      mediaConnectingLabelVisible: false,
      mediaParticipantNoVideoLableVisible: false,
      mediaParticipantMuteLabelVisible: false,
      mediaParticipantIdentityVisible: false,
      mediaParticipantSettingsVisible: false,
      selfMediaContainerLabelVisible: false,
      joinBtnEnabled: true,
      mActiveRoom: undefined,
      mIsLeaving: false,
      mIsConnecting: false,
      mIsLoadingCamera: false,
      mIsLoadingMic: false,
      mAudioLocalTrack: undefined,
      mVideoPreviewTrack: undefined,
      mVideoConnectedRemoteTrack: undefined,
      mVideoParticipantTrack: undefined,
      mediaContainerAttachment: undefined,
      selfMediaContainer: undefined,
      transcription: [],
      speechRecognizer: null,
      recognitionLanguage: "en-US",
      selfIdentity: "",
      participantInfo: [1, 2, 3, 4, 5, 6, 7, 8, 9].map((index) => {
        return {
          identity: null,
          sid: null,
          joined: false,
          audio: false,
          video: false,
          order: 10,
          participantIndex: index,
          blur: true,
          anonymous: false,
        };
      }),
      showFullTranscriptionList: false,
      isOpenRecipientCameraList: false,
      recipientCameraList: [],
      recipientIdentityForCameraList: null,
      wsRtp: null,
      wsConnectInterval: null,
      audioStream: null,
      recordSpeech: null,
    };
  },
  computed: {
    ...mapGetters("video", ["videoRoomParticipants", "videoRoomMediaSettings", "videoVisibilityStatus"]),
    ...mapGetters("user", ["currentToken", "currentUser", "isMobile"]),
    ...mapGetters("conversations", ["currentConversation"]),
    ...mapGetters("agency", ["currentAgency", "agencySettings"]),
    ...mapGetters("transcription", ["recognitionLanguages", "selfRecognitionLanguage", "transcribing"]),
    ...mapGetters("persist", ["userVideoRequestPersisted"]),
    ...mapState({
      videoRooms: (state) => state.video.videoRooms,
    }),
    currentVideoRoom() {
      return this.videoRooms[this.videoRooms.findIndex(room => room.room_id === this.roomId)];
    },
    isTranscriptionAvailable() {
      return !this.magicToken && this.agencySettings.enableVideoTranscription === true;
    },
    numberOfJoinedParticipants() {
      return this.participantInfo.filter(item => item.joined && !item.anonymous).length + 1;
    },
    isUIOptimized() {
      return this.isMobile && this.isMinimized;
    }
  },
  async created() {
    this.$store.commit("video/setVideoRoomWidgetBootstraped", false);
    if (!this.magicToken) {
      this.participantInfo.forEach(info => {
        info.blur = this.blurVideo;
      });
    }

    this.isLoading = true;

    if (this.magicToken) {
      this.selfIdentity = this.magicToken.token_data.recipient;
    } else {
      this.selfIdentity = this.currentAgency.agency_name;
    }

    window.addEventListener("beforeunload", this.leaveRoom);
    document.addEventListener("visibilitychange", this.handlePageVisibility);
    window.addEventListener("blur", this.handleWindowBlur);
    window.addEventListener("focus", this.handleWindowFocus);

    if (!this.anonymous) {
      EventBus.$on("video.enable-camera", async (identity) => {
        if (this.selfIdentity == identity) {
          if (this.mVideoPreviewTrack) {
            await this.toggleCamera(false);
          }
          await this.toggleCamera(true);
        }
      });

      EventBus.$on("video.disable-camera", async (identity) => {
        if (this.selfIdentity == identity) {
          await this.toggleCamera(false);
        }
      });

      EventBus.$on("video.enable-audio", (identity) => {
        if (!this.isMicOn && this.selfIdentity == identity) {
          this.toggleMic(true);
        }
      });

      EventBus.$on("video.disable-audio", (identity) => {
        if (this.isMicOn && this.selfIdentity == identity) {
          this.toggleMic(false);
        }
      });

      EventBus.$on("video.toggle-camera", async (data) => {
        const {
          identity,
          deviceId
        } = data;
        if (this.selfIdentity == identity) {
          if (deviceId) {
            const findDevice = this.mVideoSettings.cameraList.find(
              (item) => item.deviceId === deviceId
            );
            if (findDevice) {
              await this.loadCamera(deviceId);
              return;
            }
          }
          const currentDeviceIndex = this.mVideoSettings.cameraList.findIndex((item) => item.deviceId === this.mVideoSettings.cameraSelectedId);
          const deviceCounts = this.mVideoSettings.cameraList.length;
          if (currentDeviceIndex >= 0 && deviceCounts > 1) {
            const device = currentDeviceIndex < deviceCounts - 1 ?
              this.mVideoSettings.cameraList[currentDeviceIndex + 1] :
              this.mVideoSettings.cameraList[0];
            await this.loadCamera(device.deviceId);
          }
        }
      });

      EventBus.$on("change-recognition-language", async (data) => {
        const {
          identity,
          data: lang
        } = data;
        if (identity == this.selfIdentity) {
          this.setSelfLanguage(identity, lang);
        }
      });
    }

    EventBus.$on("start-transcription", () => {
      if (!this.transcribing) {
        if (!this.anonymous) {
          this.startSpeechRecognition();
        } else {
          this.$store.commit("transcription/setTranscribing", true);
        }
      }
    });

    EventBus.$on("stop-transcription", () => {
      if (!this.anonymous) {
        this.stopSpeechRecognition();
      } else {
        this.$store.commit("transcription/setTranscribing", false);
      }
    });

    if (!this.checkBrowserSupport()) {
      this.error = "Video not supported on this device.";
      return false;
    }

    this.mediaContainerVisible = true;

    if (this.participantRole === "dispatch") {
      this.mIsDispatch = true;
    }

    await this.bootstrap();

    this.$store.commit("video/setVideoRoomWidgetBootstraped", true);

    if (this.token && this.resourceId) {
      this.connectToRoom();
    }
  },
  methods: {
    ...mapActions("conversations", ["createMessage"]),
    showFullTranscriptions() {
      this.showFullTranscriptionList = true;
    },
    async setParticipantLang(participantIdentity, lang) {
      if (!this.magicToken) {
        EventBus.$emit("video.remote-control", {
          event: "changeRecognitionLanguage",
          conversationId: this.$route.params.conversationId,
          identity: participantIdentity,
          data: lang,
        });
      }
    },
    async setSelfLanguage(identity, lang) {
      if (this.magicToken) {
        this.$store.commit("transcription/setSelfRecognitionLanguage", lang);
        
        await this.$ConveyApi.recipientsApi.updateRecipientLanguage({
          numberOrId: this.magicToken.recipient_id,
          language: lang.split('-')[0]
        });
      }
    },
    agentStartTranscription() {
      this.$store.commit("transcription/setTranscribing", true);
      EventBus.$emit("request-start-transcription", {
        conversationId: this.$route.params.conversationId,
      });
      this.startSpeechRecognition();
    },
    agentStopTranscription() {
      this.$store.commit("transcription/setTranscribing", false);
      EventBus.$emit("request-stop-transcription", {
        conversationId: this.$route.params.conversationId
      });
      this.stopSpeechRecognition();
    },
    startSpeechRecognition() {
      if (!this.wsRtp) {
        this.openWsRtp();
      }
      if (this.wsRtp.readyState === WebSocket.OPEN) {
        this.initAudio();
      } else {
        this.wsRtp.addEventListener("open", () => {
          this.initAudio();
        });
        this.wsRtp.addEventListener("close", () => {
          if (!this.wsConnectInterval) {
            this.wsConnectInterval = setInterval(() => {
              if (this.wsRtp.readyState !== WebSocket.OPEN && this.recordSpeech !== null) {
                this.openWsRtp();
              }
            }, 1000);
          }
        });
        this.wsRtp.addEventListener("error", () => {
          if (!this.wsConnectInterval) {
            this.wsConnectInterval = setInterval(() => {
              if (this.wsRtp.readyState !== WebSocket.OPEN && this.recordSpeech !== null) {
                this.openWsRtp();
              }
            }, 1000);
          }
        });
      }
    },
    stopSpeechRecognition() {
      if (this.audioStream) {
        this.audioStream.getAudioTracks().forEach((track) => {
          track.stop();
        });
      }
      if (this.recordSpeech) {
        this.recordSpeech.stopRecording();
        this.recordSpeech = null;
      }
      this.closeWsRtp();
    },
    initAudio() {
      (async () => {
        if (!this.recordSpeech) {
          const constraints = { audio: true };
          this.audioStream = await navigator.mediaDevices.getUserMedia(constraints);
          this.recordSpeech = new RecordRTC(this.audioStream, {
            type: "audio",
            recorderType: StereoAudioRecorder,
            mimeType: "audio/wav",
            timeSlice: 480,
            desiredSampRate: 8000,
            numberOfAudioChannels: 1, // Allows for stereo recording
            ondataavailable: async (blob) => {
              const arrayBuffer = await blob.arrayBuffer();
              if (this.transcribing && this.isMicOn && this.wsRtp.readyState === WebSocket.OPEN) {
                this.wsRtp.send(arrayBuffer);
              }
            }
          });
        }

        this.sendAudio();
      })();
    },
    sendAudio() {
      if (this.recordSpeech) {
        this.recordSpeech.startRecording();
      }
    },
    openWsRtp() {
      const recipientNumber = this.magicToken ? this.magicToken.token_data.recipient : this.currentConversation.primary_recipient;
      const agencyId = this.magicToken ? this.magicToken.agency_id : this.currentAgency.agency_id;
      const conversationId = this.magicToken ? this.magicToken.token_data.conversation_id : this.$route.params.conversationId;
      const wsUrl = `${process.env.VUE_APP_WS_RTP_HOST
        }?conversationId=${conversationId
        }&phoneNumber=${recipientNumber
        }&primaryRecipient=${recipientNumber
        }&agencyId=${agencyId
        }&recipientLanguage=${this.magicToken ? "en-US" : this.selfRecognitionLanguage
        }&senderLanguage=${this.magicToken ? "en-US" : this.selfRecognitionLanguage
        }&source=video&recipientFlow=${this.magicToken ? "true" : "false"
        }&identity=${this.selfIdentity}&role=${this.magicToken ? "recipient" : "responder"
        }&encoding=opus`;
      this.wsRtp = new WebSocket(wsUrl);
      this.wsRtp.binaryType = "arraybuffer";
    },
    closeWsRtp() {
      if (this.wsRtp)
        this.wsRtp.close();
    },
    enableRemoteAudio(participant) {
      EventBus.$emit("video.remote-control", {
        event: !participant.audio ? "enableAudio" : "disableAudio",
        conversationId: this.$route.params.conversationId,
        identity: participant.identity,
      });
    },
    enableRemoteVideo(participant) {
      EventBus.$emit("video.remote-control", {
        event: !participant.video ? "enableCamera" : "disableCamera",
        conversationId: this.$route.params.conversationId,
        identity: participant.identity,
      });
    },
    toggleRemoteCamera(participant) {
      console.log("toggleRemoteCamera >>>>", participant, this.videoRoomMediaSettings)
      if (participant.video) {
        const currentVideoRoomMediaSettings = this.videoRoomMediaSettings[this.$route.params.conversationId];
        const participantVideoRoomMediaSettings = currentVideoRoomMediaSettings[participant.identity];

        if (participantVideoRoomMediaSettings && participantVideoRoomMediaSettings.cameraList) {
          this.openRecipientCameraList(participant.identity, participantVideoRoomMediaSettings.cameraList);
        } else {
          EventBus.$emit("video.remote-control", {
            event: "toggleCamera",
            conversationId: this.$route.params.conversationId,
            identity: participant.identity,
          });
        }
      }
    },
    getCameraListHeight(list) {
      const maxHeight = (list.length + 1) * 30 + 12;
      return maxHeight > 300 ? "300px" : `${maxHeight}px`;
    },
    selectedRecipientCamera(data) {
      const {
        identity,
        deviceId
      } = data;

      EventBus.$emit("video.remote-control", {
        event: "toggleCamera",
        conversationId: this.$route.params.conversationId,
        identity,
        deviceId,
      });

      this.closeRecipientCameraList();
    },
    openRecipientCameraList(identity, list) {
      this.recipientIdentityForCameraList = identity;
      this.isOpenRecipientCameraList = true;
      this.recipientCameraList = [...list];
    },
    closeRecipientCameraList() {
      this.isOpenRecipientCameraList = false;
      this.recipientCameraList = [];
    },
    blurRemoteVideo(participant) {
      const participantInfo = this.participantInfo.find(item => item.identity == participant.identity);
      if (participantInfo) {
        participantInfo.blur = !participantInfo.blur;
      }
    },
    requestLocation(participant) {
      EventBus.$emit("video.remote-control", {
        event: "shareLocation",
        conversationId: this.$route.params.conversationId,
        identity: participant.identity
      });
      this.$toastr("success", `Location requested for ${participant.identity}`);
    },
    async removeParticipant(participant) {
      try {
        await this.$ConveyApi.videoApi.deleteVideoRoomParticipant({
          roomId: this.roomId,
          participantId: participant.sid,
        });
      } catch (err) {
        console.log(err);
      }
    },
    checkBrowserSupport() {
      if (!isSupported) {
        const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        if (isiOS) {
          this.showNotSupportedDialog(false);
        } else {
          this.showNotSupportedDialog(true);
        }
        return false;
      }
      return true;
    },
    async bootstrap() {
      await this.$nextTick(() => {
        this.mediaContainerAttachment = document.getElementById("media-container-attachment");
        this.selfMediaContainer = document.getElementById("self-media-container");
      });

      this.videoService = new VideoService();
      await this.checkCameraMicPermissions();
    },
    shareVideoSettings() {
      if (this.magicToken) {
        EventBus.$emit("share-information", {
          conversationId: this.magicToken.token_data.conversation_id,
          type: "share-video-settings",
          data: {
            mVideoSettings: this.mVideoSettings,
            identity: this.selfIdentity,
          },
        });
      }
    },
    async handleDeviceChange() {
      const oldDeviceSettings = {...this.mVideoSettings};
      const oldDevices = [...this.mediaDevices];
      const newDevices = await this.videoService.getMediaDevices();
      const addedDevices = newDevices.filter(device => !oldDevices.find(d => d.deviceId === device.deviceId));
      const removedDevices = oldDevices.filter(device => !newDevices.find(d => d.deviceId === device.deviceId));

      if (addedDevices.length > 0) {
        this.mediaDevices = newDevices;
        this.mVideoSettings = await this.videoService.getMediaDeviceList(
          newDevices,
          this.mIsDispatch
        );

        this.shareVideoSettings();

        addedDevices.forEach(mediaDevice => {
          switch (mediaDevice.kind) {
            case 'videoinput':
              this.loadCamera(mediaDevice.deviceId);
              break;
            case 'audioinput':
              this.loadMic(mediaDevice.deviceId);
              break;
            case 'audiooutput':
              this.loadSpeaker(mediaDevice.deviceId);
              break;
          }
        });
      } else if (removedDevices.length > 0) {
        this.mediaDevices = newDevices;
        this.mVideoSettings = await this.videoService.getMediaDeviceList(
          newDevices,
          this.mIsDispatch
        );

        this.shareVideoSettings();

        removedDevices.forEach(mediaDevice => {
          switch (mediaDevice.kind) {
            case 'videoinput':
              if (oldDeviceSettings.cameraSelectedId === mediaDevice.deviceId) {
                this.loadCamera(this.mVideoSettings.cameraSelectedId);
              }
              break;
            case 'audioinput':
              if (oldDeviceSettings.micSelectedId === mediaDevice.deviceId) {
                this.loadMic(this.mVideoSettings.micSelectedId);
              }
              break;
            case 'audiooutput':
              if (oldDeviceSettings.speakerSelectedId === mediaDevice.deviceId) {
                this.loadSpeaker(this.mVideoSettings.speakerSelectedId);
              }
              break;
          }
        });
      }
    },
    async checkCameraMicPermissions() {
      const errorMessage = await this.videoService.requestMediaAccess();
      if (errorMessage) {
        this.showCameraMicBlockedNotice(true, errorMessage);
      } else {
        await this.loadCameraAndMic();

        if (!this.magicToken) {
          if (this.startLocalAudio) {
            this.toggleMic(true);
          }
          if (this.startLocalVideo) {
            this.toggleCamera(true);
          }
        } else {
          this.shareVideoSettings();
        }

        navigator.mediaDevices.addEventListener("devicechange", this.handleDeviceChange);
      }
    },
    async loadCameraAndMic() {
      this.isMicOn = this.startLocalAudio;
      this.isCameraOn = this.startLocalVideo;

      await this.loadMediaDeviceList();

      if (this.startLocalVideo) {
        await this.loadCamera(this.mVideoSettings.cameraSelectedId);
      }

      if (this.startLocalAudio) {
        await this.loadMic(this.mVideoSettings.micSelectedId);
      }

      this.mediaBtnContainerVisible = true;
    },
    async loadMediaDeviceList() {
      this.mediaDevices = await this.videoService.getMediaDevices();
      this.mVideoSettings = await this.videoService.getMediaDeviceList(
        this.mediaDevices,
        this.mIsDispatch
      );
    },
    showCameraMicBlockedNotice(showLabel, errorMessage, alertMsg = "") {
      if (showLabel) {
        this.mediaBlockedLabelVisible = true;
      }

      alert(alertMsg ? alertMsg : "Camera and microphone are blocked!");

      this.mediaContainerLoadingVisible = false;
      this.mediaCameraOffLabelVisible = false;

      if (errorMessage) {
        /* eslint-disable */
        console.error(errorMessage);
      }
    },
    showNotSupportedDialog(isChrome) {
      let message = "Video is not supported on your device. You may have to use a different browser.";
      if (!isChrome) {
        message = "Video is not supported on your device. You may have to use a different browser";
      }
      alert(message);
    },
    async loadCamera(cameraSelectedId) {
      if (!this.mIsLoadingCamera) {
        this.mIsLoadingCamera = true;
        this.mediaBlockedLabelVisible = false;

        if (!this.isConnected) {
          await this.loadPreviewCamera(cameraSelectedId);
        } else {
          await this.loadConnectedCamera(cameraSelectedId);
        }

        this.mIsLoadingCamera = false;
      }
    },
    async loadPreviewCamera(cameraSelectedId) {
      this.selfMediaContainerLabelVisible = false;
      if (this.isCameraOn) {
        this.mediaContainerLoadingVisible = true;
        this.mediaCameraOffLabelVisible = false;

        if (this.mVideoPreviewTrack) {
          this.videoService.muteLocalTrack(this.mVideoPreviewTrack, this.mActiveRoom);
          this.mVideoPreviewTrack = undefined;
        }

        const localTrack = await createLocalVideoTrack({
          deviceId: {
            exact: cameraSelectedId
          },
        }).catch((error) => {
          this.showCameraMicBlockedNotice(true, error, "We can't find your camera!");
          this.isCameraOn = false;
        });

        if (localTrack) {
          this.mVideoPreviewTrack = localTrack;

          if (this.mActiveRoom) {
            await this.mActiveRoom.localParticipant.publishTrack(localTrack);
          }

          const child = await this.videoService.attachTrack(localTrack, this.selfMediaContainer, cameraSelectedId);
          this.mediaContainerLoadingVisible = false;
        }
      } else {
        this.mediaCameraOffLabelVisible = true;
        this.mediaContainerLoadingVisible = false;
      }
    },
    async loadConnectedCamera(cameraSelectedId) {
      this.mVideoSettings.cameraSelectedId = cameraSelectedId;
      if (this.isCameraOn) {
        this.mediaCameraOffLabelVisible = false;

        // Remote Video to broadcast
        // ** Do not call this block to publish multiple tracks from one device **
        if (this.mVideoConnectedRemoteTrack) {
          this.videoService.muteLocalTrack(this.mVideoConnectedRemoteTrack, this.mActiveRoom);
          this.mVideoConnectedRemoteTrack = undefined;
        }

        const remoteTrack = await createLocalVideoTrack({
          deviceId: {
            exact: cameraSelectedId
          },
        }).catch((error) => {
          this.showCameraMicBlockedNotice(true, error, "We can't find your camera!");
          this.isCameraOn = false;
        });

        if (remoteTrack) {
          this.mVideoConnectedRemoteTrack = remoteTrack;

          if (this.mActiveRoom) {
            await this.mActiveRoom.localParticipant.publishTrack(remoteTrack);
          }

          const child = await this.videoService.attachTrack(remoteTrack, this.selfMediaContainer, cameraSelectedId);
        }
      } else if (!this.mVideoConnectedRemoteTrack) {
        this.mediaCameraOffLabelVisible = true;
      }
    },
    loadSpeaker(speakerSelectedId) {
      this.mVideoSettings.speakerSelectedId = speakerSelectedId;
      try {
        const audioWrapper = document.getElementById(`video-room-widget-${this.roomId}`);
        const audioElements = audioWrapper.querySelectorAll('audio');
        audioElements.forEach(async (audioElement) => {
          if ('setSinkId' in audioElement) {
            await audioElement.setSinkId(speakerSelectedId);
          }
        });
      } catch(err) {
        console.log(err);
      }
    },
    async loadMic(micSelectedId) {
      this.mVideoSettings.micSelectedId = micSelectedId;
      if (!this.mIsLoadingMic) {
        this.mIsLoadingMic = true;

        if (this.isMicOn) {
          if (this.mAudioLocalTrack) {
            this.videoService.muteLocalTrack(this.mAudioLocalTrack, this.mActiveRoom);
            this.mAudioLocalTrack = undefined;
          }

          const localTrack = await createLocalAudioTrack({
            deviceId: {
              exact: micSelectedId
            },
          }).catch((error) => {
            this.showCameraMicBlockedNotice(false, error, "We can't find your microphone!");
            this.isMicOn = false;
          });

          if (localTrack) {
            this.mAudioLocalTrack = localTrack;
            if (this.mActiveRoom) {
              this.mActiveRoom.localParticipant.publishTrack(localTrack);
            }
          }
        }

        // this.resumeSpeechRecognition();

        this.mIsLoadingMic = false;
      }
    },
    async connectToRoom() {
      if (!this.mIsConnecting) {
        this.mIsConnecting = true;
        this.mediaConnectingLabelVisible = true;

        const videoRoom = await this.videoService.connectToVideoRoom(
          this.token,
          this.resourceId,
          this.startLocalVideo,
          this.startLocalAudio
        ).catch((error) => {
          this.showError(error);
        });

        this.shareVisibilityInformation(document.hasFocus() ? !document.hidden : false);

        if (videoRoom) {
          this.roomJoined(videoRoom);
        }

        if (this.startTranscription && !this.magicToken) {
          this.agentStartTranscription();
        }

        this.mIsConnecting = false;
        this.isLoading = false;
      }
    },
    roomJoined(room) {
      this.mActiveRoom = room;
      const self = this;
      setTimeout(() => {
        self.videoService.muteLocalTrack(this.mVideoPreviewTrack, this.mActiveRoom);
      }, 250);

      this.mediaCameraOffLabelVisible = false;
      this.mediaContainerLoadingVisible = true;
      this.mediaParticipantIdentityVisible = false;
      this.mediaParticipantSettingsVisible = false;

      this.mediaConnectingLabelVisible = false;
      this.joinBtnEnabled = true;

      if (room.participants.size > 0) {
        this.mediaWaitingLabelVisible = false;
      } else {
        this.mediaWaitingLabelVisible = true;
      }

      room.participants.forEach((participant) => {
        this.participantConnected(participant);
      });

      room.on('participantConnected', (participant) => {
        this.mediaWaitingLabelVisible = false;
        this.participantConnected(participant);
      });

      room.on('participantDisconnected', (participant) => {
        this.detachParticipantTracks(participant);
        const participantInfo = this.participantInfo.find(item => item.sid == participant.sid);
        if (participantInfo) {
          participantInfo.joined = false;
          participantInfo.identity = null;
          participantInfo.sid = null;
          participantInfo.anonymous = false;
          participantInfo.video = false;
          participantInfo.audio = false;
          participantInfo.blur = true;
          participantInfo.order = 10;
        }
        this.participantInfo.forEach(item => {
          if (item.joined) {
            item.order -= 1;
          }
        })
      });

      room.on('disconnected', () => {
        room.participants.forEach((participant) => {
          this.detachParticipantTracks(participant);
        });

        this.leaveRoom();
      });

      this.isConnected = true;

      this.loadCamera(this.mVideoSettings.cameraSelectedId);

      if (this.mAudioLocalTrack) {
        this.mActiveRoom.localParticipant.publishTrack(this.mAudioLocalTrack);
      } else {
        this.loadMic(this.mVideoSettings.micSelectedId);
      }
    },
    detachParticipantTracks(participant) {
      const tracks = this.getTracks(participant);
      tracks.forEach((track) => {
        this.videoService.detachTrack(track);
      });
    },
    getTracks(participant) {
      return Array.from(participant.tracks.values())
        .filter((publication) => {
          return publication;
        }).map((publication) => {
          return publication.track;
        });
    },
    participantConnected(participant) {
      const participantInfo = this.participantInfo.find(item => !item.joined)
      if (participantInfo) {
        const container = document.getElementById(`media-container-${participantInfo.participantIndex}`)
        participantInfo.identity = participant.identity
        participantInfo.sid = participant.sid;
        participantInfo.anonymous = participant.identity == "Authorized Participant";
        const joinedParticipants = this.participantInfo.filter(item => item.joined);
        participantInfo.order = joinedParticipants.length > 0 ? Math.max(...joinedParticipants.map(item => item.order)) : 1;
        participantInfo.joined = true;

        participant.tracks.forEach(async (publication) => {
          console.log("[participantConnected:loop] trackPublished: ", publication);
          await this.trackPublished(publication, container, participant);
        });

        participant.on('trackPublished', async (publication) => {
          console.log("[participantConnected] trackPublished: ", publication);
          await this.trackPublished(publication, container, participant);
        });

        participant.on('trackUnpublished', (publication) => {
          console.log("[participantConnected] trackUnpublished: ", publication);
          this.trackUnpublished(publication, participant);
        });

        participant.on('trackSubscribed', async (track) => {
          console.log("[participantConnected] trackSubscribed: ", track);
        });

        participant.on('trackUnsubscribed', (track) => {
          console.log("[participantConnected] trackUnsubscribed: ", track);
        });
      }

      if (!this.magicToken && this.startTranscription && this.transcribing) {
        EventBus.$emit("request-start-transcription", {
          conversationId: this.$route.params.conversationId,
        })
      }
    },
    trackUnpublished(publication, participant) {
      const {
        sid,
        identity
      } = participant;
      const participantInfo = this.participantInfo.find(item => item.sid == sid);

      if (publication.kind === 'video') {
        participantInfo.video = false;
      } else {
        participantInfo.audio = false
      }
    },
    handleTrackStatus(track, sid) {
      const participantInfo = this.participantInfo.find(item => item.sid == sid)
      track.on('disabled', () => {
        if (track.kind === 'video') {
          participantInfo.video = false;
        }
        if (track.kind === 'audio') {
          participantInfo.audio = false;
        }
      });
      track.on('enabled', () => {
        if (track.kind === 'video') {
          participantInfo.video = true;
        }
        if (track.kind === 'audio') {
          participantInfo.audio = true;
        }
      });
    },
    async trackPublished(publication, container, participant) {
      const {
        identity,
        sid
      } = participant;
      if (publication.isSubscribed) {
        await this.attachRemoteTrack(publication.track, container, identity == "Authorized Participant");
        this.handleTrackStatus(publication.track, sid);
      }

      publication.on('subscribed', async (track) => {
        await this.attachRemoteTrack(track, container, identity == "Authorized Participant");
        this.handleTrackStatus(track, sid);
      });

      publication.on('unsubscribed', (track) => {
        this.detachRemoteTrack(track);
      });

      const participantInfo = this.participantInfo.find(item => item.sid == sid)
      if (publication.kind === 'video') {
        participantInfo.video = true;
      } else {
        participantInfo.audio = true;
      }
    },
    detachRemoteTrack(track) {
      if (track.kind === 'video') {
        if (!this.isCameraOn) { }
        this.mVideoParticipantTrack = undefined;
      }

      this.videoService.detachTrack(track);
    },
    async attachRemoteTrack(track, container, anonymous) {
      if (track.kind === 'video') {
        this.mVideoParticipantTrack = track;
      }
      await this.videoService.attachTrack(track, container, this.mVideoSettings.speakerSelectedId);
    },
    leaveClick() {
      this.mediaContainerLoadingVisible = true;
      this.leaveRoom();
    },
    async leaveRoom() {
      EventBus.$emit("video.leave-room", {
        roomId: this.roomId
      });
      if (!this.mIsLeaving) {
        this.mIsLeaving = true;
        this.isConnected = false;

        if (this.mActiveRoom) {
          this.mActiveRoom.disconnect();
          this.mActiveRoom = undefined;
        }

        this.joinBtnEnabled = true;
        this.mIsLeaving = false;
      }
    },
    micOffBtnClick() {
      if (!this.anonymous) {
        this.toggleMic(true)
      }
    },
    micOnBtnClick() {
      if (!this.anonymous) {
        this.toggleMic(false)
      }
    },
    toggleMic(toggleOn) {
      this.isMicOn = toggleOn;

      if (toggleOn) {
        this.loadMic(this.mVideoSettings.micSelectedId);
      } else {
        this.videoService.muteLocalTrack(this.mAudioLocalTrack, this.mActiveRoom);
        this.mAudioLocalTrack = undefined;
      }
    },
    cameraOffBtnClick() {
      if (!this.anonymous) {
        this.toggleCamera(true);
      }
    },
    cameraOnBtnClick() {
      if (!this.anonymous) {
        this.toggleCamera(false);
      }
    },
    toggleCamera(toggleOn) {
      this.isCameraOn = toggleOn;

      if (toggleOn) {
        this.loadCamera(this.mVideoSettings.cameraSelectedId);
      } else {
        if (!this.isConnected) {
          this.mediaContainerLoadingVisible = false
        }
        this.selfMediaContainerLabelVisible = false;

        this.videoService.muteLocalTrack(this.mVideoPreviewTrack, this.mActiveRoom);
        this.mVideoPreviewTrack = undefined;

        this.videoService.muteLocalTrack(this.mVideoConnectedRemoteTrack, this.mActiveRoom);
        this.mVideoConnectedRemoteTrack = undefined;

        if (!this.isConnected || !this.mVideoParticipantTrack) {
          this.mediaCameraOffLabelVisible = true;
        }
      }
    },
    showError(error) {
      this.videoService.muteLocalTrack(this.mVideoPreviewTrack, this.mActiveRoom);
      this.videoService.muteLocalTrack(this.mAudioLocalTrack, this.mActiveRoom);
      this.mediaBtnContainerVisible = false;
      this.mediaConnectingLabelVisible = false;
      this.joinBtnEnabled = true;

      if (error.code === 0) {
        this.showCameraMicBlockedNotice(true, undefined);
      } else {
        this.mediaContainerLoadingVisible = false;
        this.mediaBlockedLabelVisible = false;
        this.mediaCameraOffLabelVisible = false;
        if (error === 'MAX-ATTEMPTS') {
          this.mediaMaxAttemptsLabelVisible = true;
        } else if (error === 'TIMEOUT') {
          this.mediaIsTimeoutLabelVisible = true;
        } else {
          this.mediaErrorLabelVisible = true;
          /* eslint-disable */
          console.log(error);
        }
      }
    },
    shareVisibilityInformation(visibility) {
      if (this.magicToken) {
        const data = {
          event: "visibilityChange",
          identity: this.selfIdentity,
          conversationId: this.magicToken.token_data.conversation_id,
          data: { visibility }
        };
        EventBus.$emit("shareVisibilityInformation", data)
      }
    },
    handlePageVisibility() {
      this.shareVisibilityInformation(!document.hidden);
    },
    handleWindowBlur() {
      this.shareVisibilityInformation(false);
    },
    handleWindowFocus() {
      this.shareVisibilityInformation(!document.hidden);
    }
  },
  watch: {
    transcribing(val) {
      if (!val) {
        this.showFullTranscriptionList = false;
      }
    },
    videoVisibilityStatus: {
      handler(newVal, oldVal) {
        if (newVal && oldVal) {
          Object.keys(newVal).forEach(identity => {
            if (!newVal[identity] && oldVal[identity]) {
              const participantIndex = this.participantInfo.findIndex(p => p.identity === identity);
              if (participantIndex >= 0) {
                this.participantInfo[participantIndex].blur = false;
              }
            }
          });
        }
      },
      deep: true,
      immediate: true
    },
  },
  beforeDestroy() {
    if (this.transcribing) {
      this.stopSpeechRecognition();
    }

    navigator.mediaDevices.removeEventListener("devicechange", this.handleDeviceChange);

    this.leaveRoom();

    setTimeout(() => {
      this.toggleCamera(false);
      this.toggleMic(false);
    }, 3000);

    if (!this.anonymous) {
      EventBus.$off("video.enable-camera");
      EventBus.$off("video.disable-camera");
      EventBus.$off("video.enable-audio");
      EventBus.$off("video.disable-audio");
      EventBus.$off("video.toggle-camera");
      EventBus.$off("start-transcription");
    }
  },
};
</script>

<style lang="scss">
.video-room-widget {
  width: 100%;
  height: min-content;
  position: relative;

  &__media-container {
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
    border: 2px solid #202124;
    background-color: #202124;
    visibility: visible;
    z-index: 1;

    >div.media-controls-container {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 2;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-wrap: wrap;

      >div.media-container {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        &.active {
          width: 50%;
          aspect-ratio: 4 / 3;
          visibility: visible;

          &.full-screen {
            width: 100%;
          }
        }

        &.inactive {
          width: 0;
          height: 0;
          visibility: hidden;
        }

        >div.media-participant-top {
          position: absolute;
          top: 0;
          right: 0;
          height: 2rem;
          display: flex;
          justify-content: flex-end;
          align-items: center;
          padding: 0 8px;
          gap: 4px;

          &.anonymousParticipant {
            top: 0;
            left: 0;
            width: 100%;
            height: auto;

            >span {
              white-space: unset !important;
            }
          }

          >span.media-participant-identity-label {
            color: white;
            font-size: 12px;
            font-weight: bold;
            padding: 2px;
            background-color: rgba(0, 0, 0, 0.5);
            backdrop-filter: blur(4px);
            white-space: nowrap;
          }

          >div.media-participant-settings {
            display: flex;
            align-items: center;
            gap: 4px;
          }
        }

        >div.media-participant-bottom {
          width: 100%;
          position: absolute;
          bottom: 0;
          left: 0;
          height: 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 8px;

          >div.media-participant-status {
            display: flex;
            align-items: center;
            gap: 4px;
          }

          >div.media-participant-controls {
            display: flex;
            align-items: center;
          }
        }
      }
    }

    >div.media-container-attachment {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-wrap: wrap;
      position: relative;
      z-index: 1;
      background-position: center;
      background-repeat: no-repeat;

      >div.media-container {
        &.remote-media-container {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: black;
          background-position: center;
          background-repeat: no-repeat;
          background-size: 50%;
          z-index: 1;

          video {
            z-index: -1;
            object-fit: cover;
          }

          &.blurVideo {
            >div.media-participant-overlay {
              position: absolute;
              backdrop-filter: blur(15px);
              width: 100%;
              height: 100%;
              z-index: 2;
              top: 0;
              left: 0;
            }

            >video {
              z-index: 1;
            }
          }

          &.stripesBlurVideo {
            >div.media-participant-overlay {
              position: absolute;
              // backdrop-filter: blur(5px);
              // background: repeating-linear-gradient(
              //   45deg,
              //   transparent,
              //   transparent 10px,
              //   rgba(255, 255, 255, 0.2) 10px,
              //   rgba(255, 255, 255, 0.2) 20px
              // );
              background: black;
              width: 100%;
              height: 100%;
              z-index: 2;
              top: 0;
              left: 0;
              font-size: 1.25rem;
              font-weight: bold;
              display: flex;
              align-items: center;
              justify-content: center;
              text-align: center;
            }

            >video {
              z-index: 1;
            }
          }

          &.active {
            width: 50%;
            aspect-ratio: 4 / 3;
            visibility: visible;

            &.full-screen {
              width: 100%;
            }
          }

          &.inactive {
            width: 0;
            height: 0;
            visibility: hidden;
          }

          &.video-enabled {
            >video {
              visibility: visible;
            }
          }

          &.video-disabled {
            >video {
              visibility: hidden;
            }
          }
        }

        &.self-media-container {
          position: absolute;
          bottom: 24px;
          right: 12px;
          z-index: 2;

          &.active {
            width: 20%;
            max-width: 120px;
            visibility: visible;

            >video {
              border: 2px solid white;
              border-radius: 2px;
              object-fit: cover;
            }
          }

          &.inactive {
            width: 0;
            height: 0;
            visibility: hidden;
          }
        }
      }
    }

    &.ui-optimized {
      visibility: hidden;
      aspect-ratio: unset;
      height: 0px;
    }
  }

  &__transcription {
    width: 100%;
    height: 50px;
    background-color: black;
    position: relative;
    z-index: 2;

    &--full {
      position: absolute;
      width: 100%;
      left: 0;
      bottom: 0;
    }
  }

  &__switchcamera {
    display: none;
    width: 100%;
    background-color: black;
    position: relative;
    z-index: 3;
    transition: all 0.3s ease;

    &.show-list {
      display: block;
    }
  }

  &__controls {
    .transcribe-control {
      >button {
        &.ui-optimized {
          padding: 4px 10px !important;
          font-size: 12px;
        }

        svg {
          width: 20px;
          fill: white;
        }
      }
    }

    .device-select {
      &.ui-optimized {
        ul.dropdown-menu {
          margin: 0 !important;
          max-height: 300px;
          overflow-y: auto;

          li {
            .dropdown-item {
              padding: 8px 0;
              font-size: 12px;
              max-width: 300px;
              display: inline-flex;
              align-items: center;

              >span {
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }
          }
        }
      }
    }
  }

}

.convey-video-setting .dropdown-menu {
  padding: 0;

  >.dropdown-item {
    padding: 0.4rem 1rem;
  }
}

.convey-video-setting .dropdown-menu .dropdown-item {
  padding: 0.4rem 1rem;
}
</style>
